// Logs plugin.
import * as actionTypes from './interface/actionTypes'
import * as actions from './interface/actions'
import { logManager } from './index'

// Libraries.
import { put, takeEvery } from 'redux-saga/effects'

// The SDK's name for the default/global log level.
const defaultType = 'DEFAULT'

/**
 * Taker saga for "set log level" actions.
 * @method setLevelEntry
 */
export function * setLevelEntry () {
  yield takeEvery(actionTypes.SET_LEVEL, setLogLevel)
}

/**
 * Taker saga for "set log handler" actions.
 * @method setHandlerEntry
 */
export function * setHandlerEntry () {
  yield takeEvery(actionTypes.SET_HANDLER, setLogHandler)
}

/**
 * Functionality saga for "set log level" actions.
 * @method setLogLevel
 * @param  {Object} action
 * @param  {string} action.level
 * @param  {string} [action.type]
 */
export function * setLogLevel (action) {
  const { level, type } = action.payload

  try {
    if (!type || type === defaultType) {
      // Update the Manager's default level.
      logManager.setLevel(level)
      // Update all Loggers' levels.
      logManager.getLoggers().forEach(logger => {
        logger.setLevel(level)
      })

      // Update all Logger levels in state, because changing the default level
      //    will affect all Loggers without their own explicit level set.
      yield put(actions.levelsChanged(getLevelMap(logManager)))
    } else {
      // Update the Manager's default level for this type.
      logManager.setLevel(type, level)
      // Update all Loggers of this type.
      logManager.getLoggers(type).forEach(logger => {
        logger.setLevel(level)
      })

      // Update the one type's level in state.
      yield put(actions.levelsChanged({ [type]: level }))
    }
  } catch (err) {
    const log = logManager.getLogger('LOGS')
    log.error(err.message)
  }
}

/**
 * Functionality saga for "set log handler" actions.
 * @method setLogHandler
 * @param  {Object}   action
 * @param  {Function} action.handler
 * @param  {string}   [action.type]
 */
export function * setLogHandler (action) {
  const { handler, type } = action.payload

  try {
    if (!type || type === defaultType) {
      // Update the Manager's default level.
      logManager.setHandler(handler)
      // Update all Loggers' handlers.
      logManager.getLoggers().forEach(logger => {
        logger.setHandler(handler)
      })

      // Notify that all Logger handlers [may] have changed (because changing
      //    the default handler will affect all Loggers without their own
      //    handler explicitly set).
      yield put(actions.handlersChanged(getHandlerMap(logManager)))
    } else {
      // Update the Manager's default handler for this type.
      logManager.setHandler(type, handler)
      // Update all Loggers of this type.
      logManager.getLoggers(type).forEach(logger => {
        logger.setHandler(handler)
      })

      // Notify that the one type's handler has changed.
      yield put(actions.handlersChanged({ [type]: handler }))
    }
  } catch (err) {
    const log = logManager.getLogger('LOGS')
    log.error(err.message)
  }
}

/**
 * Helper function.
 * Gets the log levels for every Logger type (and default).
 * @method getLevelMap
 * @return {Object} Mapping of Logger type to its log level.
 */
export function getLevelMap (logManager) {
  // Get unique types from all Loggers.
  const loggers = logManager.getLoggers()
  const types = [...new Set(loggers.map(logger => logger.type))]

  const levels = {}
  // Add the default level to the beginning.
  levels[defaultType] = logManager.getLevel()

  // Get the level for each Logger type.
  types.forEach(type => {
    levels[type] = logManager.getLevel(type)
  })

  return levels
}

/**
 * Helper function.
 * Gets the log handler for every Logger type (and default).
 * @method getHandlerMap
 * @return {Object} Mapping of Logger type to its log handler.
 */
export function getHandlerMap (logManager) {
  // Get unique types from all Loggers.
  const loggers = logManager.getLoggers()
  const types = [...new Set(loggers.map(logger => logger.type))]

  const handlers = {}
  // Add the default level to the beginning.
  handlers[defaultType] = logManager.getHandler()

  // Get the handler for each Logger type.
  types.forEach(type => {
    handlers[type] = logManager.getHandler(type)
  })

  return handlers
}
